// Utility functions for generating SEO data

export const generateSEOData = (report) => {
  if (!report) return {};

  // Clean and truncate text for descriptions
  const cleanText = (text, maxLength = 160) => {
    if (!text) return '';
    return text
      .replace(/[#*\-\n\r]/g, ' ')  // Remove markdown characters and newlines
      .replace(/\s+/g, ' ')         // Replace multiple spaces with single space
      .trim()
      .substring(0, maxLength)
      .trim();
  };

  // Generate title based on report result and content
  const generateTitle = () => {
    const resultPrefix = {
      'True': 'Verified True',
      'False': 'Verified False', 
      'Partially True': 'Partially Verified',
      'Unverified': 'Unverified'
    }[report.result] || 'Fact Check';

    const shortTitle = report.title?.substring(0, 50) || 'News Article';
    return `${resultPrefix}: ${shortTitle} | VeryNews AI Fact Check`;
  };

  // Generate description
  const generateDescription = () => {
    const resultText = {
      'True': 'verified as true',
      'False': 'verified as false',
      'Partially True': 'partially verified',
      'Unverified': 'found to be unverifiable with available sources'
    }[report.result] || 'fact-checked';

    const contentSnippet = cleanText(report.content, 100);
    return `AI-powered fact-check analysis: This news content has been ${resultText} by VeryNews. ${contentSnippet}`;
  };

  // Generate keywords
  const generateKeywords = () => {
    const baseKeywords = [
      'fact check',
      'news verification', 
      'AI analysis',
      'misinformation detection',
      'VeryNews',
      'news authenticity'
    ];

    const resultKeywords = {
      'True': ['verified news', 'authentic news', 'true news'],
      'False': ['fake news', 'misinformation', 'false news'],
      'Partially True': ['partial truth', 'mixed accuracy', 'partially true'],
      'Unverified': ['unverified claim', 'insufficient evidence', 'unconfirmed']
    }[report.result] || [];

    // Extract potential keywords from title (simple approach)
    const titleKeywords = report.title
      ?.toLowerCase()
      .split(/\s+/)
      .filter(word => word.length > 3 && !['this', 'that', 'with', 'from', 'they', 'have', 'will', 'been', 'said'].includes(word))
      .slice(0, 5) || [];

    return [...baseKeywords, ...resultKeywords, ...titleKeywords].join(', ');
  };

  // Generate current page URL
  const generateURL = () => {
    if (typeof window !== 'undefined') {
      return window.location.href;
    }
    return `https://verynews.com/report/${report.id}`;
  };

  return {
    title: generateTitle(),
    description: generateDescription(),
    keywords: generateKeywords(),
    url: generateURL(),
    type: 'article',
    siteName: 'VeryNews - AI-Powered Fact Checking'
  };
};

export const generateHomeSEO = () => ({
  title: 'VeryNews - AI-Powered News Verification & Fact Checking',
  description: 'Verify news authenticity with advanced AI technology. VeryNews provides comprehensive fact-checking analysis to combat misinformation and ensure news credibility.',
  keywords: 'news verification, fact checking, AI analysis, misinformation detection, news authenticity, fake news detection, media literacy',
  type: 'website'
});

export const generateReportListSEO = () => ({
  title: 'Verification Reports | VeryNews AI Fact Check Archive',
  description: 'Browse through comprehensive fact-check reports and news verification analyses. Access the complete archive of AI-powered news authenticity assessments.',
  keywords: 'fact check reports, news verification archive, misinformation analysis, verified news, fake news detection, news credibility reports',
  type: 'website'
});

